import { type Address, addressSchema } from '@mevaser/shared'
import { Button, Field, Input } from '@mevaser/ui'

/** The address form's working state — every field a plain string so inputs
 *  stay controlled; {@link addressFromDraft} turns it into the stored shape. */
export interface AddressDraft {
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
}

export const EMPTY_ADDRESS_DRAFT: AddressDraft = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
}

export function addressDraftFrom(address: Address | null): AddressDraft {
  if (!address) return EMPTY_ADDRESS_DRAFT
  return {
    line1: address.line1,
    line2: address.line2 ?? '',
    city: address.city,
    state: address.state,
    postalCode: address.postalCode,
  }
}

function isBlank(draft: AddressDraft): boolean {
  return Object.values(draft).every((v) => !v.trim())
}

/**
 * An entirely blank form means "no address" (null — which also clears one on
 * a PATCH). Anything else must be a whole, valid address; the first problem is
 * thrown as a plain message because the route schema's own failure carries no
 * per-field detail.
 */
export function addressFromDraft(draft: AddressDraft): Address | null {
  if (isBlank(draft)) return null
  if (!draft.line1.trim()) throw new Error('Enter the street address')
  if (!draft.city.trim()) throw new Error('Enter the city')
  if (!draft.state.trim()) throw new Error('Enter the two-letter state')
  if (!draft.postalCode.trim()) throw new Error('Enter the ZIP code')
  const parsed = addressSchema.safeParse({
    line1: draft.line1,
    line2: draft.line2.trim() || null,
    city: draft.city,
    state: draft.state,
    postalCode: draft.postalCode,
  })
  if (!parsed.success) {
    throw new Error(parsed.error.issues[0]?.message ?? 'Check the address and try again')
  }
  return parsed.data
}

export function CustomerAddressFields({
  value,
  onChange,
}: {
  value: AddressDraft
  onChange: (next: AddressDraft) => void
}) {
  return (
    <fieldset className="space-y-3">
      <div className="flex items-center justify-between">
        <legend className="text-sm font-medium">Address</legend>
        {isBlank(value) ? null : (
          <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_ADDRESS_DRAFT)}>
            Clear address
          </Button>
        )}
      </div>
      <Field label="Street address">
        {(props) => (
          <Input
            {...props}
            value={value.line1}
            autoComplete="address-line1"
            onChange={(e) => onChange({ ...value, line1: e.target.value })}
          />
        )}
      </Field>
      <Field label="Apartment, suite, etc.">
        {(props) => (
          <Input
            {...props}
            value={value.line2}
            autoComplete="address-line2"
            onChange={(e) => onChange({ ...value, line2: e.target.value })}
          />
        )}
      </Field>
      <div className="grid grid-cols-[1fr_4.5rem_6.5rem] gap-2">
        <Field label="City">
          {(props) => (
            <Input
              {...props}
              value={value.city}
              autoComplete="address-level2"
              onChange={(e) => onChange({ ...value, city: e.target.value })}
            />
          )}
        </Field>
        <Field label="State">
          {(props) => (
            <Input
              {...props}
              value={value.state}
              maxLength={2}
              autoComplete="address-level1"
              onChange={(e) =>
                onChange({ ...value, state: e.target.value.toUpperCase().slice(0, 2) })
              }
            />
          )}
        </Field>
        <Field label="ZIP">
          {(props) => (
            <Input
              {...props}
              value={value.postalCode}
              inputMode="numeric"
              autoComplete="postal-code"
              onChange={(e) => onChange({ ...value, postalCode: e.target.value })}
            />
          )}
        </Field>
      </div>
    </fieldset>
  )
}
